import React, { createContext, useContext } from "react";
import { useAuthStore } from "@/stores/authStore";

// Re-export TelegramUser type for backward compatibility
export type { TelegramUser } from "@/types";

import type { TelegramUser, TelegramThemeParams } from "@/integrations/telegram/telegramService";

interface TelegramContextType {
  isTelegram: boolean;
  telegramUser: TelegramUser | null;
  isInitialized: boolean;
  isAuthenticating: boolean;
  currentTheme: TelegramThemeParams | null;
  isThemeApplied: boolean;
  colorScheme: 'light' | 'dark' | null;
}

const TelegramContext = createContext<TelegramContextType | null>(null);

export const useTelegram = () => {
  const context = useContext(TelegramContext);
  const store = useAuthStore();

  if (!context) {
    // For backward compatibility, return data from store directly
    return {
      isTelegram: store.isTelegram,
      telegramUser: store.telegramUser,
      isInitialized: store.isInitialized,
      isAuthenticating: store.isAuthenticating,
      currentTheme: store.currentTheme,
      isThemeApplied: store.isThemeApplied,
      colorScheme: store.colorScheme,
    };
  }
  return context;
};

export const TelegramProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const store = useAuthStore();

  // Create value object for context
  const value: TelegramContextType = {
    isTelegram: store.isTelegram,
    telegramUser: store.telegramUser,
    isInitialized: store.isInitialized,
    isAuthenticating: store.isAuthenticating,
    currentTheme: store.currentTheme,
    isThemeApplied: store.isThemeApplied,
    colorScheme: store.colorScheme,
  };

  return (
    <TelegramContext.Provider value={value}>
      {children}
    </TelegramContext.Provider>
  );
};